import React, { Component, PureComponent, memo } from 'react';
import { Grid as GridBase, Card, Toolbar, CardContent, Typography, Button, CircularProgress, Link, Dialog, DialogContent, DialogActions } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Form as FormBase, Field as FieldBase, FormSpy as FormSpyBase } from 'react-final-form';
import { FieldArray as FieldArrayBase } from 'react-final-form-arrays';
import TextField from '../../../components/text_field';
import axios from 'axios';
import arrayMutators from 'final-form-arrays';
import FastSelect from '../../../components/fast_suggest_field';
import createDecorator from 'final-form-calculate';
import ProgressBar from '../../testing/progressbar';
import isEqual from 'lodash.isequal';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DownloadButton from '../../../components/download_button';
import Fuse from 'fuse.js';
import { DndContext, useDroppable, useDraggable, rectIntersection } from '@dnd-kit/core';
import Config from '../../../config';
import Helpers, { getTaxonomy } from '../../../helpers';
import DropZone from '../../files/dropzone';

// Relax third-party / legacy MUI types to `any` (behavior unchanged).
const Grid: any = GridBase;
const Form: any = FormBase;
const Field: any = FieldBase;
const FormSpy: any = FormSpyBase;
const FieldArray: any = FieldArrayBase;

const treeItemSx = {
    color: 'text.secondary',
    backgroundColor: 'white',
    position: 'relative',
    '& .MuiTreeItem-content': {
        width: '100%',
        boxSizing: 'border-box',
        padding: '0 !important',
    },
    '& .MuiTreeItem-label': {
        width: '100%',
        overflow: 'hidden',
    },
};

const LabelRoot = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
    overflow: 'hidden',
}));


const LabelText = styled('div')({
    fontWeight: 'inherit',
    flexGrow: 0,
    flexShrink: 0,
    textAlign: 'left',
    width: '20.75rem',
    display: 'flex',
    paddingRight: '1rem',
    overflow: 'hidden',
});

const CategoryCode = styled('div')({
    width: '18.75rem',
    flexShrink: 0,
    display: 'flex',
    paddingRight: '1rem',
    overflow: 'hidden',
});

const MappedCategory = styled('div')({
    flex: 1,
    display: 'flex',
    minWidth: 0,
    overflow: 'hidden',
});

const Actions = styled('div')({
    flex: 0,
    marginLeft: "1rem"
});

const DragWrapper = styled('div')({
    cursor: "grab",
    outline: 'none'
});

const categoryMatchLogic = (suggestion, value) => suggestion.label.toLowerCase().indexOf(value) > -1;

function CustomStyledTreeItem(props: any) {
    const { labelText, code, mappedCategory, depth, name, mappingOptions, removeItem, children, nodeId, ...other } = props;
    let { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: props.name,
    });
    const { setNodeRef: setDropperRef, isOver } = useDroppable({
        id: props.name,
    });
    const { setNodeRef: setChildDropperRef, isOver: isOverChild } = useDroppable({
        id: props.name + ".Children",
    });
    const style: any = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` //
    } : {};
    if (isDragging) {
        style.zIndex = '300';
        style.boxShadow = '0px 15px 15px 0 rgba(34, 33, 81, 0.1)';
        style.borderRadius = '4px';
        style.width = 'fit-content';
        style.position = 'fixed';
    }
    let dropperOffset = isOverChild ? 100 : 0;
    let dropperStyle = isOver || isOverChild ? { zIndex: '100', backgroundColor: '#737373', height: '8px', marginLeft: (depth * 16) + dropperOffset + 'px' } : {};
    return (
        <div style={{ position: 'relative' }}>
            {!isDragging && <div ref={setDropperRef} style={{ position: 'absolute', width: '100px', height: '100%' }}></div>}
            {!isDragging && <div ref={setChildDropperRef} style={{ position: 'absolute', left: '100px', width: 'calc(100% - 100px)', height: '100%' }}></div>}
            <TreeItem
                itemId={nodeId}
                label={
                    <>
                        <LabelRoot style={{ width: isDragging ? 'fit-content' : undefined }}>
                            <div style={{ width: `${16 * depth}px`, flexShrink: 0 }} />
                            <DragWrapper {...listeners} {...attributes}><DragIndicatorIcon color="inherit" style={{ marginRight: '8px' }} /></DragWrapper>
                            <LabelText style={{ width: `calc(20.75rem - ${16 * depth}px)` }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                <Field style={{ flex: '1' }} component={TextField} name={`${name}.Name`} disabled={isDragging} />
                            </LabelText>
                            <CategoryCode style={{ display: isDragging ? 'none' : undefined }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                <Field style={{ flex: '1' }} component={TextField} name={`${name}.Id`} />
                            </CategoryCode>
                            <MappedCategory style={{ display: isDragging ? 'none' : undefined }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                <Field style={{ flex: '1' }} matchLogic={categoryMatchLogic} controlled={true} component={FastSelect} hideUnmatched={true} options={mappingOptions.options} name={`${name}.MapTo`} />
                            </MappedCategory>
                            <Actions style={{ display: isDragging ? 'none' : undefined }}>
                                <Button variant="outlined" onClick={removeItem}>Remove</Button>
                            </Actions>
                        </LabelRoot>
                        <div style={{ height: '0px', borderRadius: '4px', position: 'relative', ...dropperStyle }}></div>
                    </>
                }
                style={style}
                ref={setNodeRef}
                sx={treeItemSx}
                {...other}
                children={isDragging ? null : children}
            />

        </div>
    );
}

const SortableItem = CustomStyledTreeItem;

const categoryMap = ({ fields, mappingOptions, selected, onClick, depth, collection }) =>
    <div>{fields.map((childName, index) => {
        return <RecursiveCategory remove={() => fields.remove(index)} collection={collection} index={index} mappingOptions={mappingOptions} name={`${childName}`} selected={selected} depth={depth} key={index} />
    })}</div>;

class RecursiveCategoryInternal extends Component<any, any> {
    render() {
        let { name, mappingOptions, remove, nodeId, depth, index, collection } = this.props;
        name = this.props.input.name;
        nodeId = this.props.input.value.nodeId;
        return <FieldArray subscription={{}} name={`${name}.Children`}>
            {({ fields }) => <>
                <SortableItem index={index} collection={collection} removeItem={remove} mappingOptions={mappingOptions} nodeId={nodeId} name={name} depth={depth}>
                    {fields.length ? fields.map((childName, index) => <RecursiveCategory remove={() => fields.remove(index)} collection={collection} index={index} mappingOptions={mappingOptions} name={`${childName}`} depth={depth + 1} nodeId={childName} key={childName} />) : ''}
                </SortableItem>

            </>}
        </FieldArray>;
    }
}

const MemoCategory = memo(RecursiveCategoryInternal, isEqual);

class RecursiveCategoryField extends Component<any, any> {
    render() {
        return <Field {...this.props} component={MemoCategory} />
    }
}

const RecursiveCategory = memo(RecursiveCategoryField, isEqual);

class ChildArray extends Component<any, any> {
    render() {
        return (
            <FieldArray subscription={{/*value: true*/ }} {...this.props}>
                {categoryMap}
            </FieldArray>
        );
    }
}

class CategoryTree extends PureComponent<any, any> {
    render() {
        let { selected, mappingOptions, defaultExpanded, ...other } = this.props;
        return <SimpleTreeView
            disableSelection
            defaultExpandedItems={defaultExpanded}
            itemChildrenIndentation={0}
            slots={{
                collapseIcon: ArrowDropDownIcon,
                expandIcon: ArrowRightIcon,
                endIcon: () => <div style={{ width: 24 }} />
            }}
            {...other}
        >
            <ChildArray name='Children' collection='Children' mappingOptions={mappingOptions} depth={0} />
        </SimpleTreeView>;
    }
}

const getCount = (category: any, init?: number): { Mapped: number; Total: number } => {
    let ret: { Mapped: number; Total: number } = { Mapped: 0, Total: init || 0 };
    if (category.MapTo) {
        ret.Mapped += 1;
    }
    if (category.Children) {
        category.Children.map(r => getCount(r, 1)).reduce((a, b) => { a.Mapped += b.Mapped; a.Total += b.Total; return a; }, ret);
    }
    return ret;
}

const calculator = createDecorator({
    field: /.MapTo$|CalculateCount/,
    updates: (value: any, name: any, allValues: any) => {
        let count = getCount(allValues);
        return { MappedCount: count.Mapped, TotalCount: count.Total };
    }
});

const renderForm = ({ handleSubmit, pristine, reset, submitting, form, setForm, hideSave, mappingOptions, modifiers, collisionDetection, showTaxonomy, importCategories, exportCategories, downloadTemplate, defaultExpanded, form: { mutators } }: any) => {
    return (
        <form onSubmit={handleSubmit} ref={f => setForm && setForm(f)}>
            <div style={{ textAlign: 'left', marginBottom: '0.5rem', display: 'flex' }}>
                <Button style={{ marginRight: '1rem' }} variant="outlined" onClick={mutators.newCategory}>New category</Button>
                <Button style={{ marginRight: '1rem' }} variant="outlined" onClick={importCategories}>Import</Button>
                <DownloadButton style={{ marginRight: '1rem' }} variant="outlined" onClick={exportCategories}>Export</DownloadButton>
                <Button style={{ marginRight: '1rem' }} variant="outlined" onClick={showTaxonomy}>Taxonomy</Button>
                <DownloadButton style={{ marginRight: '1rem' }} variant="outlined" onClick={async () => await mutators.autoMatch(form)}>Auto match</DownloadButton>
                {!hideSave && <DownloadButton style={{ marginRight: '1rem' }} disabled={pristine} onClick={handleSubmit} variant="outlined">Save</DownloadButton>}
                <FormSpy subscription={{ values: true }}>
                    {({ values }) => <div style={{ flex: '1' }}><ProgressBar completeText="All categories mapped." passed={values.MappedCount} total={values.TotalCount} /></div>}
                </FormSpy>
                <Field name="CalculateCount" style={{ display: 'none' }} component={TextField} />
            </div>

            <div style={{ display: 'flex', textAlign: 'left' }}>
                <div style={{ width: "25rem" }}><Typography variant="h6">Category</Typography></div>
                <div style={{ width: "20rem" }}><Typography variant="h6">Code</Typography></div>
                <div style={{ width: "20rem", flex: 1 }}><Typography variant="h6">Mapped Category</Typography></div>
            </div>
            <FormSpy subscription={{ values: true }}>
                {({ values }) => values.Children && Object.keys(values.Children).length ? '' : <div style={{ flex: '1', marginTop: '5rem', marginBottom: '5rem' }}>
                    <Typography>You do not have any categories set up. Please <Link component={'button'} style={{ lineHeight: '1.5rem' }} onClick={mutators.newCategory}>add a new category</Link> or <Link component='button' style={{ lineHeight: '1.5rem' }} onClick={importCategories}>import your categories</Link> to begin. You may download a template <Link component='button' style={{ lineHeight: '1.5rem' }} onClick={downloadTemplate}>here</Link>.
                    </Typography></div>}
            </FormSpy>
            <DndContext modifiers={modifiers} onDragEnd={mutators.onDragEnd} onDragStart={mutators.onDragStart} collisionDetection={collisionDetection}>
                <CategoryTree mappingOptions={mappingOptions} defaultExpanded={defaultExpanded} />
            </DndContext>
        </form>
    )
};

class TaxonomyTree extends PureComponent<any, any> {
    mapTaxonomyTree = (taxonomy) => {
        return Object.keys(taxonomy).map(r => <TreeItem itemId={taxonomy[r].Id} key={taxonomy[r].Id} label={r}>{taxonomy[r].Children ? this.mapTaxonomyTree(taxonomy[r].Children) : ''}</TreeItem>);
    }
    render() {
        let { taxonomy, expanded, onNodeToggle, ...otherProps } = this.props;
        return (
            <SimpleTreeView
                slots={{
                    collapseIcon: ExpandMoreIcon,
                    expandIcon: ChevronRightIcon
                }}
                disableSelection
                expandedItems={expanded}
                onExpandedItemsChange={onNodeToggle}
                {...otherProps}
            >
                {this.mapTaxonomyTree(taxonomy)}
            </SimpleTreeView>
        );
    }
}

class Categories extends Component<any, any> {
    state: any = { selected: [], mappingOptions: [], loading: true, showError: false, showImport: false, showTaxonomy: false, expandedTaxonomy: [] };
    async componentDidMount() {
        let categories = await getTaxonomy();
        let mappingOptions = this.getMappingOptions(categories);
        let fuseOpts = {
            keys: ["label", "name"],
            minMatchCharLength: 4,
            threshold: 0.4,
            ignoreLocation: true
        };
        let formState = await this.getFormState();
        let fuse = new Fuse(mappingOptions, fuseOpts);
        this.setState({ mappingOptions: { options: mappingOptions }, taxonomy: categories, fuse, loading: false, ...formState });
    }
    getFormState = async () => {
        let companyCategories = (await axios.get(Config.api + "/api/v1/product/categories")).data.Records;
        let formState = this.buildTree(companyCategories);
        let count = getCount(formState);
        formState.TotalCount = count.Total;
        formState.MappedCount = count.Mapped;
        let defaultExpanded = this.getExpanded(formState);
        if (count.Total > 100) {
            defaultExpanded = [];
        }
        return { categories: formState, defaultExpanded: defaultExpanded };
    }
    getExpanded = (tree: any) => {
        let ret = [];
        if (tree.nodeId && tree.Children && tree.Children.length > 0) {
            ret.push(tree.nodeId);
        }
        if (tree.Children) {
            ret = ret.concat(tree.Children.map(x => this.getExpanded(x)).reduce((a, b) => a.concat(b), []));
        }
        return ret;
    }
    buildTree = (categories: any): any => {
        let ret = { Children: [] };
        categories = categories.sort((a, b) => a.Name > b.Name ? 1 : a.Name < b.Name ? -1 : 0);
        let newCategories = categories.map((cat, i) => ({ nodeId: "c_" + i, Id: cat.Id, Name: cat.Name, MapTo: cat.MapTo, Children: [], ParentId: cat.ParentId }));
        for (let i = 0; i < newCategories.length; i++) {
            let cat = newCategories[i];
            let parentCat = !(!cat.ParentId) ? newCategories.filter(x => x.Id === cat.ParentId)[0] : null;
            if (parentCat) {
                parentCat.Children.push(cat);
            } else {
                ret.Children.push(cat);
            }
        }
        return ret;
    }
    getMappingOptions = (categories: any, prefix?: string): any => {
        let ret = [];
        if (!categories) {
            return ret;
        }
        prefix = prefix || "";
        let keys = Object.keys(categories);
        for (let i = 0; i < keys.length; i++) {
            ret.push({
                value: categories[keys[i]].Id,
                label: prefix + keys[i],
                name: keys[i]
            });
            ret = ret.concat(this.getMappingOptions(categories[keys[i]].Children, prefix + keys[i] + " > "));
        }
        return ret;
    }
    onSubmit = async (formData: any) => {
        let flatList = formData.Children.map(cat => this.flattenTree(cat)).reduce((a, b) => a.concat(b), []);
        let success = true;
        try {
            await axios.post(Config.api + "/api/v1/product/categories/bulkupdate", { Categories: flatList });
            let formState = await this.getFormState();
            this.setState({ ...formState });
        } catch (e) {
            success = false;
            let error = 'An unexpected error occurred.';
            if (e.response && e.response.data) {
                error = new Helpers().getApiErrors(e.response.data).join("\n");
            }
            this.setState({ showError: true, error: error });
        }
        if (this.props.onSubmitted) {
            this.props.onSubmitted(success);
        }
    }
    flattenTree = (category: any, parent?: any): any => {
        let ret = [];
        ret.push({ Id: category.Id, ParentId: parent, MapTo: category.MapTo, Name: category.Name });
        if (category.Children) {
            let flatList = category.Children.map(cat => this.flattenTree(cat, category.Id)).reduce((a, b) => a.concat(b), []);
            ret = ret.concat(flatList);
        }
        return ret;
    }
    recursiveMatch = (children, prefix, changeValue) => {
        for (let i = 0; i < children.length; i++) {
            if (!children[i].MapTo && children[i].Name) {
                let matches = this.state.fuse.search(children[i].Name);
                if (matches.length > 0) {
                    changeValue(prefix + `[${i}].MapTo`, v => matches[0].item.value);
                }
            }
            if (children[i].Children) {
                this.recursiveMatch(children[i].Children, prefix + `[${i}].Children`, changeValue);
            }
        }
    }
    autoMatch = (args, state, utils) => {
        let value = state.formState.values;
        utils.changeValue(state, "AutoMatching", v => true);
        //args[0].pauseValidation();
        //utils.changeValue(state, 'Children', v => { this.recursiveMatch(v, 'Children'); return v;});
        this.recursiveMatch(value.Children, 'Children', utils.changeValue.bind(this, state));
        //args[0].resumeValidation();
        utils.changeValue(state, "AutoMatching", v => false);
        utils.changeValue(state, "CalculateCount", v => !v);
    }
    count = 1;
    newCategory = (args, state, utils) => {
        utils.changeValue(state, 'Children', v => [{ nodeId: `a_${this.count++}`, MapTo: '' }].concat(v));
    }
    regenerateNodeIds = (children, counter = { value: 0 }) => {
        if (!children) return;
        children.forEach((child) => {
            child.nodeId = `n_${Date.now()}_${counter.value++}`;
            if (child.Children) {
                this.regenerateNodeIds(child.Children, counter);
            }
        });
    }
    onDragEnd = (args, state, utils) => {
        let dropData = args[0];
        if (!dropData || !dropData.active || !dropData.over) {
            return;
        }
        let tree = state.formState.values;
        let moveFromBracket = dropData.active.id.lastIndexOf("[");
        let moveFromListName = dropData.active.id.substring(0, moveFromBracket);
        let moveFromList = utils.getIn(tree, moveFromListName);
        let moveFromIndex = parseInt(dropData.active.id.substring(moveFromBracket + 1, dropData.active.id.length - 1));
        if (dropData.over.id.endsWith("]")) {
            let moveToBracket = dropData.over.id.lastIndexOf("[");
            let moveToListName = dropData.over.id.substring(0, moveToBracket);
            let moveToList = utils.getIn(tree, moveToListName);
            let moveToIndex = parseInt(dropData.over.id.substring(moveToBracket + 1, dropData.over.id.length - 1));
            if (moveToListName === moveFromListName) {
                if (moveToIndex === moveFromIndex) {
                    return;
                }
                moveToIndex += moveToIndex < moveFromIndex ? 0 : -1;
            }
            let toMove = moveFromList.splice(moveFromIndex, 1)[0];
            moveToList.splice(moveToIndex + 1, 0, toMove);

        } else {
            let moveToEntry = utils.getIn(tree, dropData.over.id.substring(0, dropData.over.id.length - 9));
            let toMove = moveFromList.splice(moveFromIndex, 1)[0];
            moveToEntry.Children = [toMove].concat(moveToEntry.Children || []);
        }
        // Regenerate nodeIds to prevent duplicate ID errors in TreeView
        this.regenerateNodeIds(tree.Children);
        utils.changeValue(state, 'Children', v => tree.Children);
    }
    onDragStart = () => {
        this.setState({ scrollY: window.scrollY });
    }
    calculateScrollOffset = (args) => {
        const { transform } = args;
        return {
            ...transform,
            y: transform.y - this.state.scrollY,
        };
    }
    customCollisionDetectionStrategy = (rects, rect) => {
        rect.top += this.state.scrollY;
        const topLevelRects = rects.filter(([id]) => id.endsWith("]"));
        const topLevelMatch = rectIntersection(topLevelRects, rect);
        if (topLevelMatch) {
            return topLevelMatch;
        }
        return rectIntersection(rects, rect);
    };
    importCategories = () => {
        this.setState({ showImport: true });
    }
    exportCategories = async () => {
        let url = (await axios.get(Config.api + `/api/v1/product/categories/export?fileType=csv`)).data.Body;
        return { href: url, fileName: "categories.csv" };
    }
    downloadTemplate = async () => {
        let url = (await this.exportCategories()).href;
        window.location.href = url;
    }
    onDrop = async (files: any) => {
        if (!files || !files.length) {
            return;
        }
        let state = { file: files[0] };
        this.setState(state);
    }
    importFile = async () => {
        try {
            let formData = new FormData();
            formData.append("file", this.state.file);
            await axios.post(Config.api + '/api/v1/product/categories/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            let formState = await this.getFormState();
            this.setState({ showImport: false, file: null, ...formState })
        } catch (e) {
            let error = 'An unexpected error occurred.';
            if (e.response && e.response.data) {
                error = new Helpers().getApiErrors(e.response.data).join("\n");
            }
            this.setState({ showError: true, error: error, file: null, showImport: false });
        }
    }
    toggleTaxonomy = (e, nodeIds) => {
        this.setState({ expandedTaxonomy: nodeIds });
    }
    showTaxonomy = () => {
        this.setState({ showTaxonomy: true });
    }
    render() {
        return (
            <>
                {this.state.loading && <CircularProgress size="8em" />}

                {this.state.categories &&
                    <Form
                        mappingOptions={this.state.mappingOptions}
                        modifiers={[this.calculateScrollOffset]}
                        onSubmit={this.onSubmit}
                        mutators={{ ...arrayMutators, autoMatch: this.autoMatch, newCategory: this.newCategory, onDragEnd: this.onDragEnd, onDragStart: this.onDragStart }}
                        decorators={[calculator]}
                        initialValues={this.state.categories}
                        subscription={{ pristine: true }}
                        collisionDetection={this.customCollisionDetectionStrategy}
                        showTaxonomy={this.showTaxonomy}
                        importCategories={this.importCategories}
                        exportCategories={this.exportCategories}
                        downloadTemplate={this.downloadTemplate}
                        defaultExpanded={this.state.defaultExpanded}
                        setForm={this.props.setForm}
                        hideSave={this.props.hideSave}
                        render={renderForm} />}

                <Dialog open={this.state.showError} onClose={() => { this.setState({ showError: false }) }} fullWidth={true} maxWidth='sm'>
                    <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
                    <DialogContent><Typography style={{ whiteSpace: "pre-wrap" }}>{this.state.error}</Typography></DialogContent>
                </Dialog>

                <Dialog open={this.state.showImport} onClose={() => { this.setState({ showImport: false }) }} fullWidth={true} maxWidth='md'>
                    <Toolbar className='lbtoolbar'>{'Import Categories'}</Toolbar>
                    <DialogContent>
                        <DropZone accept=".csv, .xlsx, .txt" style={{ width: '100%', height: '5em' }} onDrop={this.onDrop}>
                            {({ isDragAccept, isDragReject, acceptedFiles, rejectedFiles }) => {
                                if (isDragAccept || isDragReject) {
                                    return "Drop here to upload this file.";
                                }
                                return this.state.file ? this.state.file.name : "Drop a CSV/XLSX file here or click to choose a file.";
                            }}
                        </DropZone>
                    </DialogContent>
                    <DialogActions>
                        <DownloadButton color='primary' onClick={this.importFile}>Upload</DownloadButton>
                    </DialogActions>
                </Dialog>

                {this.state.taxonomy && (
                    <Dialog open={this.state.showTaxonomy} onClose={() => { this.setState({ showTaxonomy: false }) }} fullWidth={true} maxWidth='md'>
                        <Toolbar className='lbtoolbar'>{'Standard Taxonomy'}</Toolbar>
                        <DialogContent>
                            <TaxonomyTree taxonomy={this.state.taxonomy} onNodeToggle={this.toggleTaxonomy} expanded={this.state.expandedTaxonomy} />
                        </DialogContent>
                    </Dialog>
                )}
            </>
        );
    }
}

class CategoryWrapper extends Component<any, any> {
    render() {
        return (
            <Grid container spacing={2} style={{ width: '100%' }}>
                <Grid item xs={12} style={{ width: '100%' }}>
                    <Card style={{ width: '100%' }}>
                        <Toolbar className='lbtoolbar'>Categories</Toolbar>
                        <CardContent>
                            <Categories {...this.props} />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    }
}

export default CategoryWrapper;

export { Categories };
